import { useCallback, useState, useEffect } from "react";

export interface QueuedMutation<TVariables, TData, TContext> {
  id: string;
  variables: TVariables;
  execute(variables: TVariables): Promise<TData>;
  onMutate?(variables: TVariables): TContext | Promise<TContext>;
  onSuccess?(
    data: TData,
    variables: TVariables,
    context: TContext
  ): void | Promise<void>;
  onError?(
    error: unknown,
    variables: TVariables,
    context: TContext | undefined
  ): void | Promise<void>;
  onSettled?(
    data: TData | undefined,
    error: unknown | null,
    variables: TVariables,
    context: TContext | undefined
  ): void | Promise<void>;
  resolve(value: TData): void;
  reject(error: unknown): void;
}

export interface MutationQueueState {
  queueLength: number;
  currentIndex: number;
  isProcessing: boolean;
}

const mainQueue: QueuedMutation<unknown, unknown, unknown>[] = [];
let mainState: MutationQueueState = {
  queueLength: 0,
  currentIndex: 0,
  isProcessing: false,
};
const subscribers: Set<(state: MutationQueueState) => void> = new Set();

const notifySubscribers = () => {
  subscribers.forEach((callback) => callback(mainState));
};

const updateState = (updates: Partial<MutationQueueState>) => {
  mainState = { ...mainState, ...updates };
  notifySubscribers();
};

let processingPromise: Promise<void> | null = null;

const processQueue = async (): Promise<void> => {
  if (mainState.isProcessing) {
    return;
  }

  if (processingPromise) {
    await processingPromise;
    if (mainState.isProcessing) {
      return;
    }
  }

  if (mainQueue.length === 0) {
    return;
  }

  updateState({ isProcessing: true });

  processingPromise = (async () => {
    while (mainQueue.length > 0) {
      const currentMutation = mainQueue[0];
      const queueLength = mainQueue.length;

      updateState({ currentIndex: 1, queueLength });

      try {
        const data = await currentMutation.execute(currentMutation.variables);

        currentMutation.resolve(data);
      } catch (error) {
        currentMutation.reject(error);
      } finally {
        mainQueue.shift();
        updateState({
          currentIndex: mainQueue.length > 0 ? 1 : 0,
          queueLength: mainQueue.length,
        });
      }
    }

    updateState({ isProcessing: false });
    processingPromise = null;
  })();

  return processingPromise;
};

export const useMutationQueue = <TVariables, TData, TContext>() => {
  const [state, setState] = useState<MutationQueueState>(mainState);

  useEffect(() => {
    const callback = (newState: MutationQueueState) => {
      setState(newState);
    };
    subscribers.add(callback);
    setState(mainState);

    return () => {
      subscribers.delete(callback);
    };
  }, []);

  const enqueue = useCallback(
    (
      mutation: Omit<
        QueuedMutation<TVariables, TData, TContext>,
        "id" | "resolve" | "reject"
      >
    ): Promise<TData> => {
      return new Promise<TData>((resolve, reject) => {
        const queuedMutation: QueuedMutation<TVariables, TData, TContext> = {
          ...mutation,
          id: `mutation-${Date.now()}-${Math.random()}`,
          resolve,
          reject,
        };

        mainQueue.push(queuedMutation);
        const queueLength = mainQueue.length;

        updateState({
          queueLength,
          currentIndex: queueLength === 1 ? 1 : mainState.currentIndex,
        });

        void processQueue().catch((error) => {
          console.error("[MutationQueue] Error processing queue:", error);
        });
      });
    },
    []
  );

  return {
    enqueue,
    state,
  };
};
